import { useContext, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Autocomplete,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { CacheContext } from "../../../Context/cacheContext";
import { VehicleContext } from "../../../Context/vehicleContext";
import { Vocabulary } from "../../../Utils/Vocabulary";
import { shortEuropeanDate } from "../../../Utils/formatDate";
import { vehicleInformationSchema } from "../../../Validators/vehicleValidators";
import { StepActions } from "../StepActions";
import { toast } from "react-toastify";
import { useAutocompleteModal } from "./useAutocompleteModal";

interface VehicleInformationFormProps {
  onSubmit: (data: any) => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

type GenericType = { nume?: string; [key: string]: any } | null | undefined;

const VehicleInformationForm: React.FC<VehicleInformationFormProps> = ({
  onSubmit,
  onPrevious,
  isFirstStep = false,
  isLastStep = false,
}) => {
  const cacheContext = useContext(CacheContext);
  const vehicleContext = useContext(VehicleContext) as any;
  const [sortedModels, setSortedModels] = useState<any[]>([]);

  // Access vehicle from context (handles both direct and state.vehicle access)
  const vehicle = vehicleContext.state?.vehicle || vehicleContext.vehicle;

  const { control, handleSubmit, setValue, watch, trigger, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(vehicleInformationSchema),
    defaultValues: {
      vin: vehicle?.vin || "",
      tip_vehicul_r: vehicle?.tip_vehicul_r || undefined,
      brand_r: vehicle?.brand_r || undefined,
      model_r: vehicle?.model_r || undefined,
      variant: vehicle?.variant || "",
      tip_caroserie_r: vehicle?.tip_caroserie_r || undefined,
      an_fabricatie: vehicle?.an_fabricatie || undefined,
      data_inmatriculare: vehicle?.data_inmatriculare || null,
      kilometri: vehicle?.kilometri || "",
      tara_origine_r: vehicle?.tara_origine_r || undefined,
      norma_poluare_r: vehicle?.norma_poluare_r || undefined,
      culoare_r: vehicle?.culoare_r || undefined,
      optiune_culoare_r: vehicle?.optiune_culoare_r || undefined,
      tip_tractiune_r: vehicle?.tip_tractiune_r || undefined,
      combustibil_r: vehicle?.combustibil_r || undefined,
      cutie_viteze_r: vehicle?.cutie_viteze_r || undefined,
      capacitate_cilindrica: vehicle?.capacitate_cilindrica || "",
      putere_cp: vehicle?.putere_cp || "",
      nr_locuri: vehicle?.nr_locuri || 5,
      numar_portiere: vehicle?.numar_portiere || 5,
      is_new: vehicle?.is_new ?? false,
    },
  });

  // Update form when vehicle context changes (e.g., after photo detection)
  useEffect(() => {
    if (vehicle) {
      if (vehicle.vin) setValue("vin", vehicle.vin);
      if (vehicle.brand_r) setValue("brand_r", vehicle.brand_r);
      if (vehicle.model_r) setValue("model_r", vehicle.model_r);
      if (vehicle.variant) setValue("variant", vehicle.variant);
      if (vehicle.tip_caroserie_r)
        setValue("tip_caroserie_r", vehicle.tip_caroserie_r);
      if (vehicle.an_fabricatie)
        setValue("an_fabricatie", vehicle.an_fabricatie);
      if (vehicle.culoare_r) setValue("culoare_r", vehicle.culoare_r);
      if (vehicle.capacitate_cilindrica)
        setValue("capacitate_cilindrica", vehicle.capacitate_cilindrica);
      if (vehicle.putere_cp) setValue("putere_cp", vehicle.putere_cp);
      if (vehicle.tara_origine_r)
        setValue("tara_origine_r", vehicle.tara_origine_r);
      if (vehicle.kilometri) setValue("kilometri", vehicle.kilometri);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vehicle?.vin,
    vehicle?.brand_r,
    vehicle?.model_r,
    vehicle?.variant,
    vehicle?.tip_caroserie_r,
    vehicle?.an_fabricatie,
    vehicle?.culoare_r,
    vehicle?.capacitate_cilindrica,
    vehicle?.putere_cp,
    vehicle?.tara_origine_r,
    vehicle?.kilometri,
    setValue,
  ]);

  // Watch brand changes to update models
  const selectedBrand = watch("brand_r");

  // Hook for adding/editing dropdown options
  const { DropDownButton, AutocompleteModalComponent } = useAutocompleteModal({
    brandId: (selectedBrand as GenericType)?.id,
  });

  useEffect(() => {
    if ((selectedBrand as GenericType)?.id) {
      const models = cacheContext.cache?.models?.filter(
        (model: any) => model.brand_id === (selectedBrand as GenericType)?.id,
      );
      setSortedModels(models || []);
    } else {
      setSortedModels([]);
    }
  }, [selectedBrand, cacheContext.cache?.models]);

  // Save form data to context on unmount (for back navigation)
  useEffect(() => {
    return () => {
      const currentValues = getValues();
      vehicleContext.updateVehicle({
        ...vehicle,
        ...currentValues,
        tip_vehicul_id: currentValues.tip_vehicul_r?.id,
        brand_id: currentValues.brand_r?.id,
        model_id: currentValues.model_r?.id,
        tip_caroserie_id: currentValues.tip_caroserie_r?.id,
        tara_origine_id: currentValues.tara_origine_r?.id,
        norma_poluare_id: currentValues.norma_poluare_r?.id,
        culoare_id: currentValues.culoare_r?.id,
        optiuni_culoare_id: currentValues.optiune_culoare_r?.id,
        tractiune_id: currentValues.tip_tractiune_r?.id,
        combustibil_id: currentValues.combustibil_r?.id,
        cutie_viteze_id: currentValues.cutie_viteze_r?.id,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitForm = (data: any) => {
    // Transform the data to include IDs for autocomplete fields
    const transformedData = {
      ...data,
      tip_vehicul_id: data.tip_vehicul_r?.id,
      brand_id: data.brand_r?.id,
      model_id: data.model_r?.id,
      tip_caroserie_id: data.tip_caroserie_r?.id,
      tara_origine_id: data.tara_origine_r?.id,
      norma_poluare_id: data.norma_poluare_r?.id,
      culoare_id: data.culoare_r?.id,
      optiuni_culoare_id: data.optiune_culoare_r?.id,
      tractiune_id: data.tip_tractiune_r?.id,
      combustibil_id: data.combustibil_r?.id,
      cutie_viteze_id: data.cutie_viteze_r?.id,
    };

    // Save to vehicle context
    vehicleContext.updateVehicle({
      ...vehicle,
      ...transformedData,
    });

    onSubmit(transformedData);
  };


  const watchFuelType = watch("combustibil_r") as GenericType;
  const isElectric = watchFuelType?.nume?.toLowerCase().includes("electric");

  const handleNextClick = async () => {
    // Trigger validation first to show errors on fields
    const isValid = await trigger();

    if (!isValid) {
      // Show warning toast
      toast.warning(Vocabulary.fillInRequiredFields);

      // Scroll to first error field
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        // Try to find the field by name or by label
        const errorElement = document.querySelector(`[name="${firstErrorKey}"]`)
          || document.getElementById(firstErrorKey);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          (errorElement as HTMLElement).focus?.();
        }
      }
      return false;
    }

    // If valid, submit the form
    const success = await new Promise<boolean>((resolve) => {
      handleSubmit(
        (data) => {
          onSubmitForm(data);
          resolve(true);
        },
        () => resolve(false),
      )();
    });
    return success;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmitForm)}
        sx={{ width: "100%" }}
      >
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 2,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontSize: "0.7rem",
            fontWeight: 600,
          }}
        >
          {Vocabulary.vehicleInformation}
        </Typography>
        <Grid container spacing={2}>
          {/* Is New Vehicle Toggle */}
          <Grid item xs={12}>
            <Controller
              name="is_new"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(value)}
                      onChange={(e) => onChange(e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#FF6633",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#FF6633",
                        },
                      }}
                    />
                  }
                  label="Vehicul nou"
                  sx={{ mb: 1 }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            {/* VIN */}
            <Controller
              name="vin"
              control={control}
              render={({
                field: { value, onChange, ref, ...field },
                fieldState: { error },
              }) => (
                <TextField
                  {...field}
                  fullWidth
                  onChange={(e) => {
                    const filteredValue = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 17);
                    onChange(filteredValue);
                  }}
                  error={!!error}
                  helperText={error?.message}
                  value={value}
                  label="Serie Sasiu (VIN)"
                  variant="outlined"
                  size="small"
                  inputRef={ref}
                  sx={{ mb: 2 }}
                  inputProps={{
                    style: { textTransform: "uppercase" },
                    maxLength: 17,
                  }}
                />
              )}
            />

            {/* Vehicle Type */}
            <Controller
              name="tip_vehicul_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  isOptionEqualToValue={(option, value) =>
                    (option as GenericType)?.nume ===
                    (value as GenericType)?.nume
                  }
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  options={cacheContext.cache?.vehicleTypes || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Tip Vehicul"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* Brand */}
            <Controller
              name="brand_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  id="brand_r"
                  size="small"
                  disableClearable
                  disablePortal
                  PaperComponent={DropDownButton}
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    (option as GenericType)?.nume ===
                    (value as GenericType)?.nume
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                    setValue("model_r", {});
                  }}
                  options={cacheContext.cache?.brands || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Brand"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* Model */}
            <Controller
              name="model_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  id="model_r"
                  size="small"
                  disableClearable
                  disablePortal
                  PaperComponent={DropDownButton}
                  getOptionLabel={(option) => option?.nume || ""}
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.nume}
                    </li>
                  )}
                  options={sortedModels}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Model"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* Variant */}
            <Controller
              name="variant"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  fullWidth
                  onChange={onChange}
                  value={value}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Varianta"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />
              )}
            />

            {/* Body Type */}
            <Controller
              name="tip_caroserie_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option as GenericType)?.nume ===
                    (value as GenericType)?.nume
                  }
                  options={cacheContext.cache?.bodyworkTypes || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Tip Caroserie"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* An fabricatie + Kilometri — side by side */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Controller
                  name="an_fabricatie"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      onChange={onChange}
                      value={value}
                      label="An fabricatie"
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="kilometri"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    const formatKm = (val: string | number | undefined): string => {
                      if (val === undefined || val === null || val === "") return "";
                      const numStr = String(val).replace(/\s/g, "");
                      return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                    };
                    const handleKmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(e.target.value.replace(/\D/g, ""));
                    };
                    return (
                      <TextField
                        fullWidth
                        onChange={handleKmChange}
                        value={formatKm(value)}
                        label="Kilometri"
                        variant="outlined"
                        size="small"
                        inputProps={{ inputMode: "numeric" }}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>

            {/* Registration Date */}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Controller
                name="data_inmatriculare"
                control={control}
                render={({ field: { ...field } }) => (
                  <DatePicker
                    {...field}
                    format={shortEuropeanDate}
                    value={field.value ? moment(field.value) : null}
                    label="Data primei inmatriculari"
                    views={["month", "year"]}
                    maxDate={moment()}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { mb: 2 },
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>

            {/* Country of Origin */}
            <Controller
              name="tara_origine_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  options={cacheContext.cache?.countries || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Tara de origine"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Pollution Norm */}
            <Controller
              name="norma_poluare_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option as GenericType)?.id === (value as GenericType)?.id
                  }
                  options={cacheContext.cache?.pollutionNorms || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Norma de poluare"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* Color */}
            <Controller
              name="culoare_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option as GenericType)?.id === (value as GenericType)?.id
                  }
                  options={cacheContext.cache?.colors || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Culoare"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* Color Options */}
            <Controller
              name="optiune_culoare_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option as GenericType)?.id === (value as GenericType)?.id
                  }
                  options={cacheContext.cache?.colorsOptions || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Optiuni Culoare"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* Traction Type */}
            <Controller
              name="tip_tractiune_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option as GenericType)?.id === (value as GenericType)?.id
                  }
                  options={cacheContext.cache?.tractionTypes || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Tractiune"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* Fuel Type */}
            <Controller
              name="combustibil_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option as GenericType)?.id === (value as GenericType)?.id
                  }
                  options={cacheContext.cache?.fuelTypes || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Tip Combustibil"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* Gearbox */}
            <Controller
              name="cutie_viteze_r"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume || ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option as GenericType)?.id === (value as GenericType)?.id
                  }
                  options={cacheContext.cache?.gearboxes || []}
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label="Cutie de viteze"
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />

            {/* Engine Capacity + Horsepower — side by side */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller
                  name="capacitate_cilindrica"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    const fieldLabel = isElectric
                      ? Vocabulary.batteryCapacity
                      : Vocabulary.capacity;
                    const unit = isElectric ? "kWh" : "cm³";
                    return (
                      <TextField
                        fullWidth
                        onChange={onChange}
                        value={value}
                        label={fieldLabel}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <span style={{ marginLeft: 6, color: "rgba(0,0,0,0.6)" }}>
                              {unit}
                            </span>
                          ),
                        }}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Controller
                  name="putere_cp"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      onChange={onChange}
                      value={value}
                      label="Putere CP"
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Nr. Locuri + Nr. Portiere — side by side */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Controller
                  name="nr_locuri"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      onChange={onChange}
                      value={value}
                      label="Nr. Locuri"
                      variant="outlined"
                      size="small"
                      type="number"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="numar_portiere"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      onChange={onChange}
                      value={value}
                      label="Nr. Portiere"
                      variant="outlined"
                      size="small"
                      type="number"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      </Box>

      <StepActions
        onNext={handleNextClick}
        onPrevious={onPrevious}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
      />

      {/* Modal for adding/editing dropdown options */}
      <AutocompleteModalComponent />
    </Box>
  );
};

export default VehicleInformationForm;
